import { useState, useEffect, useCallback } from 'react';
import type { PublicHoliday, PublicHolidaysResponse } from '@techgitter/contracts';

let cachedHolidays: PublicHoliday[] | null = null;
let cachedYear: number | null = null;

export function usePublicHolidays(year?: number) {
  const targetYear = year ?? new Date().getFullYear();
  const [holidays, setHolidays] = useState<PublicHoliday[]>(
    cachedYear === targetYear ? (cachedHolidays ?? []) : []
  );
  const [isLoading, setIsLoading] = useState(cachedYear !== targetYear || cachedHolidays === null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    if (cachedHolidays !== null && cachedYear === targetYear) {
      setHolidays(cachedHolidays);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/public-holidays?year=${targetYear}`, { signal });
      if (!response.ok) throw new Error(`Failed to load holidays: ${response.status}`);

      const data: PublicHolidaysResponse = await response.json();
      cachedHolidays = data.items;
      cachedYear = targetYear;
      setHolidays(data.items);
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      setError(err.message || 'Failed to load public holidays');
    } finally {
      setIsLoading(false);
    }
  }, [targetYear]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const retry = useCallback(() => {
    cachedHolidays = null;
    cachedYear = null;
    load();
  }, [load]);

  return { holidays, isLoading, error, retry };
}

/**
 * Synchronous access to the cached holidays list.
 * Returns empty array if cache not yet populated.
 * Intended for use in pricing rules where holidays are already loaded.
 */
export function getHolidays(): PublicHoliday[] {
  return cachedHolidays ?? [];
}
